import React, { createContext, useContext, useEffect, useMemo } from 'react';

import { ThemeProvider } from '@material-ui/core';
import PropTypes from 'prop-types';

import { PARTNER_CONFIGS } from '../config/partners';
import { buildMuiTheme } from '../utils/buildMuiTheme';
import ReactGTM from '../utils/gtm';
import { injectPartnerFonts } from '../utils/injectFonts';
import { resolvePartnerByHost } from '../utils/resolvePartner';

/**
 * @typedef {import('../config/partners/index').PartnerConfig} PartnerConfig
 */

/**
 * @typedef {PartnerConfig & {
 * isNotBB: boolean,
 * areaLogadaUri: string,
 * }} PartnerContextValue
 */

/**
 * @type {React.Context<PartnerContextValue|null>}
 */
const PartnerContext = createContext(null);

/**
 * @param {string} faviconPath - Caminho para o favicon do parceiro
 */
const updateFavicon = (faviconPath) => {
  let link = document.querySelector('link[rel="icon"]');

  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }

  link.href = faviconPath;
};

const updateTitle = (title) => {
  if (title) {
    document.title = title;
  }
};

const updateDescription = (content) => {
  let description = document.querySelector('meta[name="description"]');

  if (!description) {
    description = document.createElement('meta');
    description.name = 'description';
    description.content = content;
    document.head.appendChild(description);
  }

  description.content = content;
};

const getAreaLogadaUri = (envToken) => {
  return process.env?.[envToken] || 'https://area-logada.cld.brasilseg.com.br';
};

/**
 * @param {{ children: React.ReactNode }} props
 * @returns {React.ReactElement}
 */
function PartnerProvider({ children }) {
  const partnerId = resolvePartnerByHost();

  // Carregar configuracao com fallback seguro para BB (RF-001, RF-003)
  const config = PARTNER_CONFIGS[partnerId] ?? PARTNER_CONFIGS.bb;
  const areaLogadaUri = getAreaLogadaUri(config.envAreaLogada);

  // Injetar fontes do parceiro ativo (sincrono, antes do render dos filhos) (RF-006)
  injectPartnerFonts(config.typography.fonts);

  // Atualizar favicon do parceiro ativo (RF-007)
  updateFavicon(config.favicon);
  updateTitle(config.displayName);
  updateDescription(config.displayName);

  // Construir tema MUI com memoizacao para evitar re-renders desnecessarios (RF-005)
  const theme = useMemo(() => buildMuiTheme(config), [config]);
  const isNotBB = config?.id !== PARTNER_CONFIGS.bb.id;

  const providerValue = useMemo(
    () => ({ ...config, isNotBB, areaLogadaUri }),
    [config, isNotBB, areaLogadaUri],
  );

  useEffect(() => {
    ReactGTM.init(config?.gtmId);
  }, []);

  return (
    <PartnerContext.Provider value={providerValue}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </PartnerContext.Provider>
  );
}

PartnerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook que retorna a configuracao completa do parceiro ativo.
 *
 * Deve ser usado dentro de um `PartnerProvider`.
 *
 * @returns {PartnerContextValue} Configuracao completa do parceiro ativo
 * @throws {Error} Se usado fora do PartnerProvider
 *
 */
function usePartner() {
  const context = useContext(PartnerContext);

  if (!context) {
    throw new Error('usePartner deve ser usado dentro de um PartnerProvider');
  }

  return context;
}

export { PartnerContext, PartnerProvider, usePartner };
